import { shortId } from '../lib/ids';
import { __ } from '../utils';
import { Avatar, Box, Tooltip } from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Toľkoto kolieskových miest má stack. Piate miesto **nie je piaty človek** -
 * keď je ľudí viac, patrí značke `+X`, takže sa naozaj vykreslia štyria.
 * Pri presne piatich sa značka nekreslí a je vidieť všetkých päť.
 */
const MAX_SLOTS = 5;

export type StackUser = {
  id: string;
  name?: string;
  surname?: string;
};

/**
 * Meno pre tooltip. Účet, ktorý sa do aplikácie ešte neprihlásil, meno
 * v `digiobcan.users` nemá - riadok tam zakladá až prvé prihlásenie, kdežto
 * priradenie ku kategórii vznikne už pozvaním. Vtedy je jediné, čím sa dá
 * odlíšiť, jeho identifikátor, takže sa ukáže ten; prázdny tooltip by
 * vyzeral ako chyba.
 */
export const userLabel = (user: StackUser): string =>
  [user.name, user.surname].filter(Boolean).join(' ') || shortId(user.id);

/**
 * Iniciály do kolieska. Prvé písmeno mena a priezviska; keď nie je ani jedno,
 * prvý znak identifikátora, aby koliesko nebolo prázdne.
 */
export const userInitials = (user: StackUser): string =>
  [user.name?.[0], user.surname?.[0]].filter(Boolean).join('').toUpperCase() ||
  shortId(user.id).charAt(0);

type Props = {
  users?: StackUser[];
  /** Priemer kolieska v px. */
  size?: number;
};

/**
 * Zodpovedné osoby ako prekrývajúce sa kolieska s menom v tooltipe.
 *
 * Stack je postavený ručne cez záporný okraj, nie cez `AvatarGroup` z MUI:
 * `AvatarGroup` si svoje deti klonuje a dopisuje im štýly, takže by ich
 * dostal `Tooltip`, ktorý ich obaľuje, a nie samotný `Avatar` - kolieska by
 * sa prestali prekrývať a stratili by orámovanie. Vlastná značka `+X`
 * s tooltipom, ktorý vymenuje **všetkých skrytých**, je druhý dôvod:
 * `renderSurplus` nevie, koho vlastne schoval.
 */
const UserAvatarStack: React.FC<Props> = ({ users = [], size = 26 }) => {
  const { shown, hidden } = React.useMemo(() => {
    if (users.length <= MAX_SLOTS) {
      return { shown: users, hidden: [] as StackUser[] };
    }
    return {
      shown: users.slice(0, MAX_SLOTS - 1),
      hidden: users.slice(MAX_SLOTS - 1),
    };
  }, [users]);

  if (!users.length) {
    return null;
  }

  const avatarSx = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.42),
    // Orámovanie farbou plátna oddelí kolieska od seba aj tam, kde sa
    // prekrývajú; bez neho splynú do jednej škvrny.
    border: '2px solid',
    borderColor: 'background.paper',
    // Záporný okraj robí prekryv. Prvé koliesko ho nemá, inak by celý stack
    // vyliezol z bunky doľava.
    ml: `-${Math.round(size * 0.3)}px`,
    '&:first-of-type': { ml: 0 },
  } as const;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {shown.map((user) => (
        <Tooltip key={user.id} title={userLabel(user)} arrow>
          <Avatar sx={avatarSx}>{userInitials(user)}</Avatar>
        </Tooltip>
      ))}

      {hidden.length ? (
        <Tooltip
          arrow
          title={
            <Box
              component='span'
              sx={{ display: 'block', whiteSpace: 'pre-line' }}
            >
              {hidden.map(userLabel).join('\n')}
            </Box>
          }
        >
          <Avatar
            sx={{
              ...avatarSx,
              bgcolor: 'action.selected',
              color: 'text.secondary',
              fontWeight: 600,
            }}
            aria-label={__('Ďalšie zodpovedné osoby')}
          >
            {`+${hidden.length}`}
          </Avatar>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export default UserAvatarStack;
